import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  CalculatorQuestionCategory,
  CalculatorQuestionType,
  Translation,
} from "@prisma/client";
import clsx from "clsx";
import { useFormatter } from "next-intl";
import ReactModal from "react-modal";
import { useLocale, useTranslations } from "use-intl";
import { CalculatorAnswerTypes } from "../../../../types/Co2CalculatorAnswerTypes";
import useUtilTranslations from "../../../hooks/useUtilTranslations";
import { CalculatorStoreState } from "../../../redux/co2calculatorSlice";
import { useAppSelector } from "../../../redux/hooks";
import {
  getIconForCategory,
  getValueFromAnswerValue,
  hasAnswerDataOptions,
} from "../../../util/calculator";
import { TSelectCurrentQuestion } from "../../../util/prismaHelper";
import BaseModal from "../../modal/BaseModal";
import styles from "./CategorySummaryModal.module.scss";

type CategorySummaryModalProps = {
  category: CalculatorQuestionCategory | null;
  questions: TSelectCurrentQuestion[] | undefined;
  error?: string;
} & Omit<ReactModal.Props, "className">;

function CategorySummaryModal({
  category,
  error,
  questions,
  ...props
}: CategorySummaryModalProps) {
  const l = useLocale();
  const t = useTranslations("Co2CalculatorIndexPage");
  const { answers, summary, session } = useAppSelector(
    (state) => state.calculator
  );
  const format = useFormatter();

  if (!category) return null;
  const value = summary[category];
  if (!value) return null;
  const percent = (value.co2e / Math.max(session!.yearlyCo2e, 1)) * 100;

  return (
    <BaseModal {...props} className={styles.modal}>
      <div className={styles.header}>
        <div className={clsx(styles.icon, styles[category.toLowerCase()])}>
          <FontAwesomeIcon icon={getIconForCategory(category)} />
        </div>
        <div className={styles.headingContainer}>
          <div className={styles.heading}>
            {t("yourAnswersFor", {
              category: t(
                `category.${
                  category.toLowerCase() as keyof IntlMessages["Co2CalculatorIndexPage"]["category"]
                }`
              ),
            })}
          </div>
          <div className={styles.subHeading}>
            {format.number((value.co2e ?? 0) / 1000, {
              maximumFractionDigits: 2,
            })}
            &nbsp;
            {t("tonnesCo2e")}
            &nbsp; (
            {format.number(percent, {
              maximumFractionDigits: 0,
              style: "unit",
              unit: "percent",
            })}
            )
          </div>
        </div>
      </div>
      {questions && questions.length > 0 ? (
        <div className={styles.content}>
          <RenderAnswers
            answers={answers}
            category={category}
            questions={questions ?? []}
            locale={l}
          />
        </div>
      ) : (
        <div className={styles.loading}>
          {error ? (
            <span>{t("errorLoadingSummaryModalData")}</span>
          ) : (
            <span>{t("loadingSummaryModalData")}</span>
          )}
        </div>
      )}
    </BaseModal>
  );
}

type Data = {
  qId: string;
  question: string;
  sortOrder: number;
  values: {
    answerId: string;
    sortOrder?: null | number;
    label?: string;
    valueText: string | number;
  }[];
};

function RenderAnswers({
  answers,
  category,
  locale,
  questions,
}: {
  answers: CalculatorStoreState["answers"];
  category: CalculatorQuestionCategory;
  questions: TSelectCurrentQuestion[];
  locale: string;
}) {
  const tUtil = useUtilTranslations();
  if (questions.length === 0) return null;

  const getLabel = (
    translations: Translation[],
    locale: string,
    { key, label }: Pick<CalculatorAnswerTypes.BaseRadioData, "key" | "label">
  ) => {
    return (
      translations.find((t) => t.key === key && t.language === locale)?.value ??
      translations.find((t) => t.key === label && t.language === locale)
        ?.value ??
      label ??
      tUtil(key as any)
    );
  };

  const a = Object.entries(answers).filter(([_, a]) =>
    Object.values(a._meta ?? {}).some((m) => m.category === category)
  );
  const result: Data[] = [];
  for (let ele of a) {
    // console.log(ele);
    const q = questions.find((q) => q.id === ele[0]);
    if (!q) {
      continue;
    }
    const qTitle = q.QuestionTranslation.find(
      (qt) => qt.key === "title" && qt.language === locale
    );

    const d: Data = {
      qId: ele[0],
      question: qTitle?.value ?? q.question,
      sortOrder: q.sortOrder,
      values: [],
    };
    if (typeof ele[1].value !== "object") {
      d.values.push({
        answerId: ele[0],
        valueText: ele[1].value.toString(),
      });
    } else {
      const value = ele[1].value;
      Object.entries(value).forEach(([answerIdOrKey, v]) => {
        if (answerIdOrKey.endsWith("_custom")) return;

        let a = q.QAnswer.find((a) => a.id === answerIdOrKey);
        if (!a) {
          Object.entries(ele[1]._meta).find(([k, v]) => {
            if (v.key === answerIdOrKey) {
              a = q.QAnswer.find((a) => a.id === k);
              return true;
            }
            return false;
          });
        }
        if (!a) {
          process.env.NODE_ENV === "development" &&
            console.warn("Answer not found", answerIdOrKey);
          return;
        }
        const answerId = a.id;
        const isCustomAnswer = `${answerId}_custom` in value;
        const metaElement = ele[1]._meta[answerId];
        const key = metaElement.key ?? answerId;
        let customUnit = metaElement.unit;

        const translations = a.QAnswerLabelTranslation;
        let aTitle = getLabel(translations, locale, {
          key: key,
          label: metaElement.label,
        });
        let label: string | undefined;

        if (a.type === CalculatorQuestionType.RANGE) {
          label = v.toString();
        }

        if (hasAnswerDataOptions(metaElement)) {
          const option = metaElement.options.find(
            (o) => o.key === v.toString()
          );
          if (option) {
            label = getLabel(translations, locale, {
              key: option.key,
              label: undefined,
            });
            if (option.hint === true) {
              label = translations.find(
                (qt) => `hint_${key}` === qt.key && qt.language === locale
              )?.value;
            } else if (typeof option.hint === "string") {
              label += " " + hintHelper(option.hint);
            }
            if (isCustomAnswer) {
              customUnit = option.unit;
            }
          }
        } else if (metaElement.hint === true) {
          const _h = translations.find(
            (qt) => `hint_${key}` === qt.key && qt.language === locale
          )?.value;
          if (label) {
            label += " " + hintHelper(_h);
          } else if (aTitle) {
            aTitle += " " + hintHelper(_h);
          }
        } else if (typeof metaElement.hint === "string") {
          if (label) {
            label += " " + hintHelper(metaElement.hint);
          } else if (aTitle) {
            if (d.qId === "energy@1") {
              v = answers["energy@1"]
                ? getValueFromAnswerValue(answers["energy@1"].value)
                : 1;
              aTitle += " " + v;
            } else {
              aTitle += " " + hintHelper(metaElement.hint);
            }
          }
        }
        console.log("Meta", isCustomAnswer, customUnit, label, aTitle);

        d.values.push({
          answerId: answerId,
          label: label ? aTitle : label,
          sortOrder: a.sortOrder,
          valueText: isCustomAnswer
            ? tUtil("ownEntry") +
              " (" +
              value[`${answerId}_custom`] +
              (customUnit ? " " + customUnit : "") +
              ")"
            : label
            ? label
            : aTitle + (customUnit ? " " + customUnit : ""),
        });
      });
    }

    result.push(d);
  }

  return (
    <>
      {result
        .sort((a1, b) => a1.sortOrder - b.sortOrder)
        .map((e) => (
          <div key={e.qId} className={styles.item}>
            <div className={styles.title}>{e.question}</div>
            {e.values
              .sort((a1, b) => (a1.sortOrder ?? 0) - (b.sortOrder ?? 0))
              .map((v) => (
                <div key={v.answerId} className={styles.value}>
                  {v.label && <span className={styles.label}>{v.label}: </span>}
                  {v.valueText}
                </div>
              ))}
          </div>
        ))}
    </>
  );
}

function hintHelper(hint?: string) {
  if (!hint) return "";
  if (!hint.startsWith("(")) {
    return "(" + hint + ")";
  }
  return hint;
}

export default CategorySummaryModal;
